import React, { useEffect, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SelectGroup from '../../../components/casino/SelectGroup';
import CusLabelSelect from '../../../components/material-ui/CusLabelSelect';
import CurrencySelect from '../../../components/shared/CurrencySelect';
import { useTranslation } from 'react-i18next';
import CusTooltip from '../../../components/material-ui/CusTooltip';
import CusInput from '../../../components/material-ui/CusInput';
import CusDatePicker from '../../../components/material-ui/CusDatePicker';
import CusDateTimePicker from '../../../components/material-ui/CusDateTimePicker';
import DatePreset from '../../../components/material-ui/DatePreset';
import useBootstrapSize from '../../../hooks/useBootstrapSize';
import CusAutocomplete from '../../../components/material-ui/CusAutocomplete';
import CusButton from '../../../components/material-ui/CusButton';
import { API_GAMES } from '../../../api/games';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../store';
import { API_REPORT } from '../../../api/report';
import { TEST_MODE } from '../../../enums/enums';
import {
  setRefcodeInfo,
  setRefcodePayload,
  setRefcodeTotal
} from '../../../store/apps/reportsSlice';
import { mockRefcodeData } from '../../../enums/mock';
import { getOnlyData } from '../../../utils';
import dayjs from 'dayjs';
import TimeZone from '../../../components/shared/TimeZone';
import useToast from '../../../hooks/useToast';

export default function ReportRefcodeFilter({ setLoader, setEmpty }) {
  const { t } = useTranslation();
  const { isMobile } = useBootstrapSize();
  const { errorToast } = useToast();
  const dispatch = useDispatch();
  const mainGroupId = useAppSelector((state) => state.user.group.main?.groupId);
  const mainGroupCurrency = useAppSelector((state) => state.user.group.main?.currency);

  const [tzOffset, setTzOffset] = useState(0);

  const gridSize = { xs: 12, sm: 6 };
  const gridSize2 = { xs: 12, sm: 3 };

  const prevWeek = getOnlyData(dayjs().subtract(7, 'day'), '00:00:00');
  const today = getOnlyData(dayjs(), '23:59:59');

  const [filterInfo, setFilterInfo] = useState({
    groupId: mainGroupId,
    refcodeId: 'skip',
    currency: mainGroupCurrency,
    dateFrom: prevWeek,
    dateTo: today
  });
  const refcodeItemAll = { label: t('All'), value: 'skip' };
  const [refcodeOptions, setRefcodeOptions] = useState([refcodeItemAll]);

  const groupId = filterInfo.groupId;
  const dateFrom = filterInfo.dateFrom;
  const dateTo = filterInfo.dateTo;

  useEffect(() => {
    if (!mainGroupId) return;
    if (groupId) return;

    setFilterInfo((prev) => ({ ...prev, groupId: prev.groupId || mainGroupId }));
  }, [mainGroupId]);

  useEffect(() => {
    if (!mainGroupCurrency) return;
    setFilterInfo((prev) => ({ ...prev, currency: prev.currency || mainGroupCurrency }));
  }, [mainGroupCurrency]);

  useEffect(() => {
    if (!groupId) return;
    API_REPORT.list.refcode
      .refcodes({ groupId })
      .then((e) => {
        const data = e.data.data;
        const options = data.map((item) => ({ label: item[1], value: item[0] }));
        setRefcodeOptions([refcodeItemAll, ...options]);
      })
      .catch(console.error);
  }, [groupId]);

  const onSaveClick = () => {
    const filter = { ...filterInfo };

    if (!filter.paramName && filter.paramValue) {
      errorToast('To use GET parameters, please enter the name of the GET parameter.');
      return;
    }

    if (filter.refcodeId === 'skip') filter.refcodeId = null;

    if (TEST_MODE) {
      dispatch(setRefcodePayload(filter));
      dispatch(setRefcodeInfo(mockRefcodeData));
      return;
    }
    setLoader(true);

    API_REPORT.list.refcode
      .fullList(filter)
      .then((e) => {
        const data = e.data.data;
        dispatch(setRefcodePayload(filter));
        dispatch(setRefcodeInfo(data.report));
        setTzOffset(data.tzOffset);
        setEmpty(!data.report.length);
      })
      .catch(console.error)
      .finally(() => setLoader(false));
  };

  return (
    <Stack gap={1.5}>
      <Stack direction={'row'} justifyContent={'space-between'} gap={1} alignItems={'center'}>
        <SelectGroup
          title="Group"
          selectGroupId={groupId}
          hideTooltip
          setSelectGroupId={(val) => {
            setFilterInfo((prev) => ({ ...prev, groupId: val }));
          }}
        />

        <TimeZone tzOffset={tzOffset} hideTooltip label="Time zone" />
      </Stack>
      <Grid container spacing={{ xs: 1, sm: 2 }}>
        <Grid size={gridSize2}>
          <CusAutocomplete
            label={'Ref. code'}
            options={refcodeOptions}
            value={refcodeOptions?.find((el) => el.value == filterInfo.refcodeId)?.label}
            onChange={(_, choseItem) =>
              setFilterInfo((prev) => ({ ...prev, refcodeId: choseItem?.value }))
            }
          />
        </Grid>
        <Grid size={gridSize2}>
          <CurrencySelect
            value={filterInfo.currency}
            setValue={(val) => setFilterInfo((prev) => ({ ...prev, currency: val }))}
          />
        </Grid>
        <Grid size={gridSize}>
          <Stack gap={0.5}>
            <Stack direction={'row'} gap={0.5} alignItems={'center'}>
              <Typography color="textSecondary">{t('GET parameter')}</Typography>
              <CusTooltip
                text={
                  'A search for the specified GET parameter name is performed in the parameter string of the link through which the player registered on the site or in the bot. The parameter value is optional.'
                }
              />
            </Stack>
            <Stack direction={'row'} gap={0.5} alignItems={'center'}>
              <CusInput
                width={'100%'}
                placeholder={'Name'}
                value={filterInfo.paramName}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilterInfo((prev) => ({ ...prev, paramName: value }));
                }}
              />
              <Typography fontSize={18}>=</Typography>
              <CusInput
                width={'100%'}
                placeholder={'Value'}
                value={filterInfo.paramValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilterInfo((prev) => ({ ...prev, paramValue: value }));
                }}
              />
            </Stack>
          </Stack>
        </Grid>
        <Grid size={gridSize}>
          <CusDateTimePicker
            label={'Start date'}
            formatChange="YYYY-MM-DD HH:mm:ss"
            value={dateFrom}
            onChange={(val) => setFilterInfo((prev) => ({ ...prev, dateFrom: val }))}
          />
        </Grid>
        <Grid size={gridSize}>
          <CusDateTimePicker
            label={'End date'}
            formatChange="YYYY-MM-DD HH:mm:ss"
            value={dateTo}
            onChange={(val) => setFilterInfo((prev) => ({ ...prev, dateTo: val }))}
            startDateForError={dateFrom}
          />
        </Grid>
        <Grid size={12} mt={-1}>
          <DatePreset
            fontSize={isMobile ? 12 : 14}
            setStartDate={(val) => setFilterInfo((prev) => ({ ...prev, dateFrom: val }))}
            setEndDate={(val) => setFilterInfo((prev) => ({ ...prev, dateTo: val }))}
          />
        </Grid>
      </Grid>

      <CusButton text="Find" variant="contained" sx={{ px: 5 }} ml="auto" onClick={onSaveClick} />
    </Stack>
  );
}
